import React, { useState, useEffect } from 'react';
import moment from 'moment';
import { getVoucherList } from "../services/Voucher";
import VoucherModal from "./VoucherModal";
import ExcelJS from 'exceljs';
import { saveAs } from 'file-saver';

function VoucherTracker({ search, statusFilter, handleDownload, setHandleDownload }) {
  const [voucherList, setVoucherList] = useState([]);
  const [currentPage, setCurrentPage] = useState(1);
  const itemsPerPage = 50;

  //voucher Modal
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [imageUrl, setImageUrl] = useState(false);
  const [linkMemories, setLinkMemories] = useState(false);

  useEffect(() => {
    if (handleDownload === true) {
      handleExportData();
      setHandleDownload(false);
    }
  }, [handleDownload]);

  function handleExportData() {
    const workbook = new ExcelJS.Workbook();
    const worksheet = workbook.addWorksheet('Voucher List');
    
    // Header information (static fields)
    worksheet.getCell('A1').value = 'Filter Search : ';
    worksheet.getCell('B1').value = search;

    worksheet.getCell('A2').value = 'Filter Status : ';
    worksheet.getCell('B2').value = statusFilter ?? 'All';

    const filter1Row = worksheet.getRow(1);
    filter1Row.font = { bold: true };
    
    const filter2Row = worksheet.getRow(2);
    filter2Row.font = { bold: true };

    const tableStartRow = 4;
    const tableHeaders = [
      'No',
      'Voucher Code',
      'Usage',
      'Usage Date',
      'Photo Result'
    ];

    worksheet.getRow(tableStartRow).values = tableHeaders;

    const headerRow = worksheet.getRow(tableStartRow);
    headerRow.font = { bold: true };
    headerRow.alignment = { horizontal: 'center' };
    headerRow.eachCell(cell => {
      cell.border = {
        top: { style: 'thin' },
        bottom: { style: 'thin' },
        left: { style: 'thin' },
        right: { style: 'thin' }
      };
    });

    const rowData = voucherList.map((item, index) => [
      index + 1,
      item.code,
      `${item.used_count}/${item.usage_count}`,
      `${item.used_at ? moment(item.used_at).format('DD/MM/YYYY HH:mm') : ''}`,
      item.transaction_url ?? '',
    ]);

    worksheet.addRows(rowData);

    rowData.forEach((_, index) => {
      const rowNumber = tableStartRow + 1 + index;
      const excelRow  = worksheet.getRow(rowNumber);
      
      excelRow.eachCell((cell) => {
        if (cell.col === 3) {
          cell.alignment = { horizontal: 'center' };
        }
        cell.border = {
          top: { style: 'thin' },
          bottom: { style: 'thin' },
          left: { style: 'thin' },
          right: { style: 'thin' }
        };
      });
    });

    // Set column widths for better visibility
    const columnWidths = [20, 25, 15, 20, 70];
    columnWidths.forEach((width, i) => {
      worksheet.getColumn(i + 1).width = width;
    });

    // Save to file
    const fileName = `voucher-tracker-${moment().format('YYYY-MM-DD_HH-mm-ss')}`;
    workbook.xlsx.writeBuffer().then(data => {
      const blob = new Blob([data], {
        type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet'
      });
      saveAs(blob, `${fileName}.xlsx`);
    });
  }
  
  const handleViewVoucher = (row) => {
    setImageUrl(row.image_url);
    setLinkMemories(row.transaction_url);
    setIsModalOpen(true)
  };

  useEffect(() => {
    fetchVoucherList();
  }, [search, statusFilter]);

  async function fetchVoucherList() {
    const res = await getVoucherList(search, statusFilter);
    if (res && Array.isArray(res.data)) {
      setVoucherList(res.data);
    } else {
      setVoucherList([]);
    }
    setCurrentPage(1);
  }

  const totalPages = Math.ceil(voucherList.length / itemsPerPage);
  const indexOfLastItem = currentPage * itemsPerPage;
  const indexOfFirstItem = indexOfLastItem - itemsPerPage;
  const currentItems = voucherList.slice(indexOfFirstItem, indexOfLastItem);

  const goToPage = (page) => {
    if (page >= 1 && page <= totalPages) {
      setCurrentPage(page);
    }
  };

  function renderPageNumbers() {
    const pageNumbers = [];
    const maxShown = 3;

    const totalMidPages = Math.min(maxShown, totalPages - 2);
    let start = Math.max(2, currentPage - Math.floor(totalMidPages / 2));
    let end = start + totalMidPages - 1;

    if (end >= totalPages) {
      end = totalPages - 1;
      start = Math.max(2, end - totalMidPages + 1);
    }

    pageNumbers.push(
      <button
        key={1}
        onClick={() => goToPage(1)}
        className={`px-3 py-1 rounded ${currentPage === 1 ? 'bg-blue-500 text-white' : 'bg-gray-200 hover:bg-gray-300'}`}
      >
        1
      </button>
    );

    if (start > 2) {
      pageNumbers.push(<span key="start-ellipsis" className="px-2">...</span>);
    }

    for (let i = start; i <= end; i++) {
      pageNumbers.push(
        <button
          key={i}
          onClick={() => goToPage(i)}
          className={`px-3 py-1 rounded ${currentPage === i ? 'bg-blue-500 text-white' : 'bg-gray-200 hover:bg-gray-300'}`}
        >
          {i}
        </button>
      );
    }

    if (end < totalPages - 1) {
      pageNumbers.push(<span key="end-ellipsis" className="px-2">...</span>);
    }

    if (totalPages > 1) {
      pageNumbers.push(
        <button
          key={totalPages}
          onClick={() => goToPage(totalPages)}
          className={`px-3 py-1 rounded ${currentPage === totalPages ? 'bg-blue-500 text-white' : 'bg-gray-200 hover:bg-gray-300'}`}
        >
          {totalPages}
        </button>
      );
    }

    return pageNumbers;
  }

  return (
    <div>
      
      <VoucherModal
        isOpen={isModalOpen}
        image_url={imageUrl}
        link_memories={linkMemories}
        onClose={() => setIsModalOpen(false)}
        title="View Transaction"
      ></VoucherModal>
      <div className="overflow-x-auto max-h-[70vh] overflow-y-auto">
        <table className="min-w-full bg-white shadow rounded-lg">
          <thead className="bg-gray-100">
            <tr>
              <th className="px-4 py-3 text-left text-sm font-medium text-gray-600">No.</th>
              <th className="px-4 py-3 text-left text-sm font-medium text-gray-600">Voucher Code</th>
              <th className="px-4 py-3 text-left text-sm font-medium text-gray-600">Usage</th>
              <th className="px-4 py-3 text-left text-sm font-medium text-gray-600">Usage Date</th>
              <th className="px-4 py-3 text-left text-sm font-medium text-gray-600">Action</th>
            </tr>
          </thead>
          <tbody>
            {currentItems.length === 0 ? (
              <tr>
                <td colSpan="5" className="px-4 py-6 text-center text-gray-500">No voucher found.</td>
              </tr>
            ) : (
              currentItems.map((v, i) => (
                <tr key={indexOfFirstItem + i} className={`border-t ${i % 2 === 0 ? '' : 'bg-gray-50'}`}>
                  <td className="px-4 py-3">{indexOfFirstItem + i + 1}</td>
                  <td className="px-4 py-3">{v.code}</td>
                  <td className="px-4 py-3">{v.used_count}/{v.usage_count}</td>
                  <td className="px-4 py-3">{v.used_at ? moment(v.used_at).format('DD/MM/YYYY HH:mm') : ''}</td>
                  <td className="px-4 py-3">
                    {v.used_at && v.image_url ? (
                      <button onClick={() => handleViewVoucher(v)} className="bg-blue-500 text-white px-3 py-1 rounded hover:bg-blue-600">
                        View
                      </button>
                    ) : (
                      <></>
                    )}
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>

      <div className="overflow-x-auto">
        {totalPages > 1 && (
          <div className="grid grid-cols-3 items-center mt-4">
            {/* Left: Total data */}
            <div className="text-gray-600 text-sm ps-2">
              Total : <span className="font-semibold">{voucherList.length}</span> vouchers
            </div>

            {/* Center: Pagination */}
            <div className="flex justify-center items-center">
              <button
                onClick={() => goToPage(currentPage - 1)}
                disabled={currentPage === 1}
                className="px-3 py-1 me-1 rounded bg-gray-200 hover:bg-gray-300 disabled:opacity-50"
              >
                Previous
              </button>

              <div className="space-x-1 flex items-center">
                {renderPageNumbers()}
              </div>

              <button
                onClick={() => goToPage(currentPage + 1)}
                disabled={currentPage === totalPages}
                className="px-3 py-1 ms-1 rounded bg-gray-200 hover:bg-gray-300 disabled:opacity-50"
              >
                Next
              </button>
            </div>

            {/* Right: kosong, bisa dipakai nanti */}
            <div></div>
          </div>
        )}
      </div>

    </div>
  );
}

export default VoucherTracker;
